import type { Issue, StatusId } from "./types";
import { humanOwnerId } from "./taskOwnership";

export type PendingTask = Pick<Issue, "id" | "identifier" | "workspaceId" | "teamId" |
  "title" | "status" | "priority" | "ownerId" | "assigneeId" | "executorAgentId" |
  "projectId" | "labelIds" | "updatedAt" | "createdAt"> & {
  /** Keep database precision for optimistic concurrency, rather than rebuilding an ISO from milliseconds. */
  version: string;
  lastActivityAt: number;
};
export type PendingReason = "missing_owner" | "missing_project" | "inactive" | "possible_duplicate";
export interface PendingFilters {
  ownerId?: string; projectId?: string; labelId?: string; status?: StatusId | "all";
  activity?: "all" | "recent" | "inactive";
  candidate?: PendingReason | "all";
}
export interface PendingCandidate {
  task: PendingTask;
  reasons: PendingReason[];
  possibleDuplicates: PendingTask[];
}
export const PENDING_INACTIVE_MS = 30 * 24 * 60 * 60_000;
const OPEN = new Set(["backlog", "todo", "in_progress", "in_review"]);
export const PENDING_REASON_LABELS: Record<PendingReason, string> = {
  missing_owner: "Sem responsável humano", missing_project: "Sem projeto",
  inactive: "Sem atividade registrada há 30 dias", possible_duplicate: "Possível duplicado",
};

/** Exact title equality after case/whitespace normalization is only a review signal. */
function duplicateKey(task: PendingTask): string | null {
  const title = task.title.normalize("NFKC").trim().replace(/\s+/g, " ").toLocaleLowerCase("pt-BR");
  return title.length >= 12 ? JSON.stringify([task.workspaceId, task.teamId, task.projectId, title]) : null;
}

export function selectPendingIssues(tasks: readonly PendingTask[], workspaceId: string,
  filters: PendingFilters = {}, now = Date.now()): PendingCandidate[] {
  const eligible = tasks.filter((task) => task.workspaceId === workspaceId && OPEN.has(task.status));
  const duplicateGroups = new Map<string, PendingTask[]>();
  for (const task of eligible) {
    const key = duplicateKey(task);
    if (key) duplicateGroups.set(key, [...(duplicateGroups.get(key) ?? []), task]);
  }
  return eligible.map((task): PendingCandidate => {
    const reasons: PendingReason[] = [];
    if (!humanOwnerId(task)) reasons.push("missing_owner");
    if (!task.projectId) reasons.push("missing_project");
    const inactive = Number.isFinite(task.lastActivityAt) && task.lastActivityAt <= now - PENDING_INACTIVE_MS;
    if (inactive) reasons.push("inactive");
    const key = duplicateKey(task);
    const possibleDuplicates = key ? (duplicateGroups.get(key) ?? []).filter((other) => other.id !== task.id) : [];
    if (possibleDuplicates.length) reasons.push("possible_duplicate");
    return { task, reasons, possibleDuplicates };
  }).filter(({ task, reasons }) => {
    const owner = humanOwnerId(task);
    return (!filters.ownerId || filters.ownerId === "all" || (filters.ownerId === "none" ? !owner : owner === filters.ownerId)) &&
      (!filters.projectId || filters.projectId === "all" || (filters.projectId === "none" ? !task.projectId : task.projectId === filters.projectId)) &&
      (!filters.labelId || filters.labelId === "all" || (filters.labelId === "none" ? task.labelIds.length === 0 : task.labelIds.includes(filters.labelId))) &&
      (!filters.status || filters.status === "all" || task.status === filters.status) &&
      (!filters.candidate || filters.candidate === "all" || reasons.includes(filters.candidate)) &&
      (!filters.activity || filters.activity === "all" || (filters.activity === "inactive"
        ? reasons.includes("inactive") : Number.isFinite(task.lastActivityAt) && task.lastActivityAt > now - PENDING_INACTIVE_MS && task.lastActivityAt <= now));
  }).sort((a, b) => b.reasons.length - a.reasons.length || a.task.lastActivityAt - b.task.lastActivityAt || a.task.id.localeCompare(b.task.id));
}

export type PendingResolutionAction = "backlog" | "duplicate" | "done";
export interface PendingResolution {
  issueId: string; version: string; action: PendingResolutionAction;
  reason: string; destinationIssueId?: string | null; evidence?: string | null;
}
export function pendingResolutionError(input: PendingResolution): string | null {
  if (!["backlog", "duplicate", "done"].includes(input.action)) return "Escolha uma ação válida.";
  if (!input.reason.trim() || input.reason.length > 4000) return "Registre um motivo de até 4.000 caracteres.";
  if (input.action === "duplicate" && (!input.destinationIssueId || input.destinationIssueId === input.issueId)) return "Escolha outro ticket como destino da duplicidade.";
  if (input.action === "done" && (!input.evidence?.trim() || input.evidence.length > 4000)) return "Registre a evidência explícita da conclusão, com até 4.000 caracteres.";
  return null;
}
